'use client';

import { useId, useState } from 'react';

import type { FaqEntry } from '@/data/shared/faq';
import { FAQ_AI_COPY, buildFaqAiPrompt } from '@/data/shared/faqAi';

import { buildAiAskUrl } from '@/components/apps/pages/solution/industrial/industrialCaseShare';
import {
  AI_SERVICES,
  AiServiceAction,
} from '@/components/apps/ui/ai/AiServiceAction';
import { IconCopyRoundLine } from '@/components/apps/ui/icon';

import type { LanguageType } from '@/types/language';

import { trackFaqEvent } from './faqAnalytics';

const SERVICE_LABELS = {
  chatgpt: 'ChatGPT',
  claude: 'Claude',
  perplexity: 'Perplexity',
  gemini: 'Gemini',
};
const focusClass =
  'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-apps-corePrimary01';

type Props = {
  locale: LanguageType;
  entry?: FaqEntry;
  question?: string;
  groupId?: string;
  faqId?: string;
};

export default function FaqAskAi({
  locale,
  entry,
  question,
  groupId,
  faqId,
}: Props) {
  const copy = FAQ_AI_COPY[locale];
  const id = useId();
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>(
    'idle',
  );
  const prompt = buildFaqAiPrompt({ locale, entry, question, groupId });
  const analyticsContext = {
    faq_id: faqId ?? 'search_empty',
    topic_id: groupId ?? 'all',
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopyState('copied');
      trackFaqEvent('faq_prompt_copy', locale, {
        ...analyticsContext,
        success: true,
      });
    } catch {
      setCopyState('failed');
      trackFaqEvent('faq_prompt_copy', locale, {
        ...analyticsContext,
        success: false,
      });
    }
  };

  return (
    <section
      className="mb-8 text-left"
      aria-label={entry ? copy.answerAction : copy.searchAction}
    >
      <h4 className="py-2 text-[16px] font-semibold tracking-tight text-apps-brandGrayBlack">
        {entry ? copy.answerAction : copy.searchAction}
      </h4>
      <div className="mt-4 max-w-180">
        <p className="text-[15px] font-medium leading-relaxed text-apps-brandGray200">
          {copy.choose}
        </p>
        <div className="mt-5 grid gap-2 sm:grid-cols-2">
          {AI_SERVICES.map((service) => (
            <AiServiceAction
              key={service.id}
              service={service}
              label={SERVICE_LABELS[service.id]}
              newWindowLabel={copy.newWindow}
              onClick={() => {
                trackFaqEvent('faq_ai_select', locale, {
                  ...analyticsContext,
                  ai_service: service.id,
                });
                window.open(
                  buildAiAskUrl(service.id, prompt),
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
            />
          ))}
        </div>
        <div className="mt-2 grid items-center gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={copyPrompt}
            className={`flex min-h-12 cursor-pointer items-center justify-between bg-[#efefec] px-4 text-left text-sm font-semibold text-apps-brandGrayBlack hover:bg-[#e4e4e0] ${focusClass}`}
          >
            {copy.copy}
            <IconCopyRoundLine size={16} aria-hidden="true" />
          </button>
          <p className="text-[13px] leading-relaxed text-apps-coreGray02">
            {copy.help}
          </p>
        </div>
        <p
          role="status"
          aria-live="polite"
          className="mt-2 text-sm text-apps-coreGrayBlack"
        >
          {copyState === 'copied'
            ? copy.copied
            : copyState === 'failed'
              ? copy.failed
              : ''}
        </p>
        {copyState === 'failed' && (
          <div className="mt-3">
            <label htmlFor={id} className="sr-only">
              {copy.promptLabel}
            </label>
            <textarea
              id={id}
              aria-label={copy.promptLabel}
              readOnly
              value={prompt}
              rows={7}
              onFocus={(event) => event.currentTarget.select()}
              className={`w-full rounded-lg border border-apps-coreProductDivider bg-apps-coreGrayWhite p-3 text-sm leading-relaxed text-apps-coreGrayBlack ${focusClass}`}
            />
          </div>
        )}
      </div>
    </section>
  );
}
